/**
 * Hook for the Marketing Copilot — handles SSE streaming, conversation management,
 * and usage tracking.
 *
 * Uses useCopilotStore.getState() inside callbacks to avoid
 * stale closures and infinite re-render loops.
 */

import { useCallback, useEffect, useRef } from 'react'
import { useCopilotStore } from '@/stores/copilotStore'
import { apiClient } from '@/services/apiClient'
import type {
  CopilotMessage,
  CopilotPersona,
  CopilotVoiceConfig,
  Visualization,
  ActionProposal,
  CopilotUsage,
  UsageSummary,
} from '@/types/copilot'

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

const getState = () => useCopilotStore.getState()

/**
 * Optional taps on the assistant's stream (marketing PR 7).
 *
 * Passed as hook options rather than extra `sendMessage` arguments so the
 * SSE contract and the `sendMessage(text)` call shape are untouched — the
 * panel's TTS reads tokens through here.
 */
export interface MarketingCopilotOptions {
  onAssistantToken?: (token: string) => void
  onAssistantComplete?: () => void
  onAssistantError?: () => void
}

function toVoiceConfig(raw: Record<string, unknown> | null): CopilotVoiceConfig | null {
  if (!raw || typeof raw !== 'object') return null
  const voiceId = raw.voice_id
  if (typeof voiceId !== 'string' || !voiceId) return null
  const config: CopilotVoiceConfig = { voiceId }
  if (typeof raw.provider === 'string') {
    config.provider = raw.provider as CopilotVoiceConfig['provider']
  }
  if (typeof raw.voice_name === 'string') config.voiceName = raw.voice_name
  if (typeof raw.model_id === 'string') config.modelId = raw.model_id
  if (typeof raw.stability === 'number') config.stability = raw.stability
  if (typeof raw.similarity_boost === 'number') config.similarityBoost = raw.similarity_boost
  if (typeof raw.style === 'number') config.style = raw.style
  if (typeof raw.use_speaker_boost === 'boolean') config.useSpeakerBoost = raw.use_speaker_boost
  if (typeof raw.speed === 'number') config.speed = raw.speed
  if (typeof raw.stt_language === 'string') config.sttLanguage = raw.stt_language
  return config
}

export function useMarketingCopilot(options: MarketingCopilotOptions = {}) {
  // State reads via individual selectors — only re-render when selected value changes
  const messages = useCopilotStore((s) => s.messages)
  const isStreaming = useCopilotStore((s) => s.isStreaming)
  const conversationId = useCopilotStore((s) => s.conversationId)
  const isOpen = useCopilotStore((s) => s.isOpen)
  const layout = useCopilotStore((s) => s.layout)
  const activeVisualizations = useCopilotStore((s) => s.activeVisualizations)
  const pendingActions = useCopilotStore((s) => s.pendingActions)
  const isVoiceActive = useCopilotStore((s) => s.isVoiceActive)
  const language = useCopilotStore((s) => s.language)
  const briefing = useCopilotStore((s) => s.briefing)
  const briefingAnomalies = useCopilotStore((s) => s.briefingAnomalies)
  const sessionUsage = useCopilotStore((s) => s.sessionUsage)
  const budgetStatus = useCopilotStore((s) => s.budgetStatus)
  const conversations = useCopilotStore((s) => s.conversations)
  const persona = useCopilotStore((s) => s.persona)

  // Held in a ref so `sendMessage` keeps its empty dep list (getState()
  // pattern above) while still calling the CURRENT callbacks — a stale
  // closure here would feed tokens to a dead TTS session.
  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  })

  const sendMessage = useCallback(async (text: string) => {
    // Read fresh state at call time via getState() to avoid stale closures
    const s = getState()
    if (!text.trim() || s.isStreaming) return

    // Add user message
    const userMsg: CopilotMessage = {
      id: generateId(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
    }
    s.addMessage(userMsg)

    // Create placeholder assistant message
    const assistantId = generateId()
    const assistantMsg: CopilotMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      visualizations: [],
      actionProposals: [],
      isStreaming: true,
    }
    s.addMessage(assistantMsg)
    s.setStreaming(true, assistantId)

    try {
      const response = await apiClient.fetch('/api/marketing/copilot/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: text,
          conversation_id: getState().conversationId,
          language: getState().language,
        }),
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(errData.detail || `HTTP ${response.status}`)
      }

      // Get conversation ID from header
      const convId = response.headers.get('X-Conversation-Id')
      if (convId && !getState().conversationId) {
        getState().setConversationId(convId)
      }

      // Process SSE stream
      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let buffer = ''
      const visualizations: Visualization[] = []
      const actionProposals: ActionProposal[] = []

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Parse SSE events from buffer
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer

        let currentEvent = ''
        let currentData = ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7)
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6)

            // Process complete event
            if (currentEvent && currentData) {
              try {
                const data = JSON.parse(currentData)

                switch (currentEvent) {
                  case 'text_delta':
                    getState().appendToMessage(assistantId, data.content || '')
                    if (data.content) optionsRef.current.onAssistantToken?.(data.content)
                    break

                  case 'visualization': {
                    const viz: Visualization = {
                      id: `viz_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                      type: data.type,
                      title: data.title || '',
                      description: data.description,
                      data: data.data || {},
                      interactive: data.interactive ?? true,
                      highlights: data.highlights,
                    }
                    visualizations.push(viz)
                    getState().addVisualization(viz)
                    break
                  }

                  case 'action_proposal': {
                    const action: ActionProposal = {
                      id: data.id || generateId(),
                      type: data.type,
                      description: data.description,
                      impactEstimate: data.impact_estimate,
                      confidence: data.confidence || 'medium',
                      before: data.before,
                      after: data.after,
                    }
                    actionProposals.push(action)
                    getState().addActionProposal(action)
                    break
                  }

                  case 'complete': {
                    const usage: CopilotUsage = {
                      provider: data.usage?.provider || '',
                      model: data.usage?.model || '',
                      promptTokens: data.usage?.prompt_tokens || 0,
                      completionTokens: data.usage?.completion_tokens || 0,
                      totalTokens: data.usage?.total_tokens || 0,
                      toolCallsCount: data.usage?.tool_calls_count || 0,
                      costUsd: data.usage?.cost_usd || { prompt: 0, completion: 0, total: 0 },
                      latencyMs: data.usage?.latency_ms || 0,
                    }

                    // Update message with final data
                    getState().updateMessage(assistantId, {
                      isStreaming: false,
                      usage,
                      visualizations: visualizations.length > 0 ? visualizations : undefined,
                      actionProposals: actionProposals.length > 0 ? actionProposals : undefined,
                    })

                    // Track session usage
                    getState().addUsage(
                      usage.totalTokens,
                      usage.promptTokens,
                      usage.completionTokens,
                      usage.costUsd.total,
                    )
                    optionsRef.current.onAssistantComplete?.()
                    break
                  }

                  case 'error':
                    getState().updateMessage(assistantId, {
                      content: `Error: ${data.error || 'Unknown error'}`,
                      isStreaming: false,
                    })
                    optionsRef.current.onAssistantError?.()
                    break
                }
              } catch {
                // Skip malformed JSON
              }
            }
            currentEvent = ''
            currentData = ''
          } else if (line === '') {
            // Empty line resets event parsing
            currentEvent = ''
            currentData = ''
          }
        }
      }
    } catch (err) {
      getState().updateMessage(assistantId, {
        content: `Error: ${err instanceof Error ? err.message : 'Failed to connect to copilot'}`,
        isStreaming: false,
      })
      optionsRef.current.onAssistantError?.()
    } finally {
      getState().setStreaming(false)
    }
  }, [])

  const loadBriefing = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/marketing/copilot/briefing')
      if (res.ok) {
        const data = await res.json()
        getState().setBriefing(data.briefing, data.anomalies)
      }
    } catch {
      // silently handle
    }
  }, [])

  /**
   * Load the copilot's configured identity.
   *
   * Rides the panel-facing `/persona` read (marketing:read), not the
   * admin-only `/config` — every copilot user needs to see who they are
   * talking to. Failures are swallowed: an unreachable persona endpoint
   * must leave a working, unnamed copilot rather than a broken panel.
   */
  const loadPersona = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/marketing/copilot/persona')
      if (!res.ok) return
      const data = await res.json()
      const persona: CopilotPersona = {
        name: data.name ?? null,
        avatarUrl: data.avatar_url ?? null,
        voiceConfig: toVoiceConfig(data.voice_config ?? null),
        voiceEnabled: data.voice_enabled !== false,
      }
      getState().setPersona(persona)
    } catch {
      // silently handle — the panel falls back to its default identity
    }
  }, [])

  const loadBudgetStatus = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/marketing/copilot/usage/budget-status')
      if (res.ok) {
        const data = await res.json()
        getState().setBudgetStatus({
          dailyCapUsd: data.daily_cap_usd,
          spentTodayUsd: data.spent_today_usd,
          remainingUsd: data.remaining_usd,
          isCapped: data.is_capped,
        })
      }
    } catch {
      // silently handle
    }
  }, [])

  const fetchUsageSummary = useCallback(async (period: 'day' | 'week' | 'month'): Promise<UsageSummary | null> => {
    try {
      const res = await apiClient.fetch(`/api/marketing/copilot/usage/summary?period=${period}`)
      if (!res.ok) return null
      const data = await res.json()
      return {
        period: data.period,
        since: data.since,
        totalRequests: data.total_requests,
        totalPromptTokens: data.total_prompt_tokens,
        totalCompletionTokens: data.total_completion_tokens,
        totalTokens: data.total_tokens,
        totalCostUsd: data.total_cost_usd,
        avgTokensPerRequest: data.avg_tokens_per_request,
        avgCostPerRequest: data.avg_cost_per_request,
        costByModel: data.cost_by_model,
        costByType: data.cost_by_type,
      }
    } catch {
      return null
    }
  }, [])

  const loadConversations = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/marketing/copilot/conversations')
      if (res.ok) {
        const data = await res.json()
        getState().setConversations(data)
      }
    } catch {
      // silently handle
    }
  }, [])

  const selectConversation = useCallback(async (id: string) => {
    try {
      const res = await apiClient.fetch(`/api/marketing/copilot/conversations/${id}`)
      if (res.ok) {
        const data = await res.json()
        getState().setConversationId(id)
        const msgs: CopilotMessage[] = (data.messages || []).map((m: Record<string, unknown>) => ({
          id: m.id || generateId(),
          role: m.role as 'user' | 'assistant',
          content: m.content as string,
          timestamp: m.timestamp ? Number(m.timestamp) : new Date(m.created_at as string).getTime(),
        }))
        getState().setMessages(msgs)
        getState().clearVisualizations()
      }
    } catch {
      // silently handle
    }
  }, [])

  const deleteConversation = useCallback(async (id: string) => {
    try {
      const res = await apiClient.fetch(`/api/marketing/copilot/conversations/${id}`, {
        method: 'DELETE',
      })
      if (res.ok) {
        // If we deleted the active conversation, start a new one
        if (getState().conversationId === id) {
          getState().newConversation()
        }
        // Refresh the list
        const listRes = await apiClient.fetch('/api/marketing/copilot/conversations')
        if (listRes.ok) {
          const data = await listRes.json()
          getState().setConversations(data)
        }
      }
    } catch {
      // silently handle
    }
  }, [])

  const approveAction = useCallback(async (actionId: string) => {
    const res = await apiClient.fetch(`/api/marketing/optimization/actions/${actionId}/review`, {
      method: 'POST',
      body: JSON.stringify({ action: 'approve' }),
    })
    if (res.ok) {
      getState().removeActionProposal(actionId)
    }
  }, [])

  const dismissAction = useCallback(async (actionId: string) => {
    const res = await apiClient.fetch(`/api/marketing/optimization/actions/${actionId}/review`, {
      method: 'POST',
      body: JSON.stringify({ action: 'dismiss' }),
    })
    if (res.ok) {
      getState().removeActionProposal(actionId)
    }
  }, [])

  return {
    // Chat
    sendMessage,
    messages,
    isStreaming,
    conversationId,

    // Panel
    isOpen,
    togglePanel: getState().toggleOpen,
    setOpen: getState().setOpen,
    layout,
    setLayout: getState().setLayout,

    // Visualizations
    activeVisualizations,

    // Actions
    pendingActions,
    approveAction,
    dismissAction,

    // Voice
    isVoiceActive,
    setVoiceActive: getState().setVoiceActive,

    // Persona (configured identity)
    persona,
    loadPersona,

    // Language
    language,
    setLanguage: getState().setLanguage,

    // Briefing
    briefing,
    briefingAnomalies,
    loadBriefing,

    // Usage
    sessionUsage,
    budgetStatus,
    loadBudgetStatus,
    fetchUsageSummary,

    // Conversation management
    conversations,
    newConversation: getState().newConversation,
    loadConversations,
    selectConversation,
    deleteConversation,
  }
}
